import type { CSSProperties } from "react";
import type { DashboardData } from "../../../contexts/dashboard-context/DashboardContext";
import { formatAdserverType } from "../../../utils";
import type { DataTableColumn } from "../Helpers/DataTable";

export const MANAGEMENT_DASHBOARD_BASE = "/management-dashboard";

export const MANAGEMENT_DASHBOARD_PATHS = {
  federationDetails: `${MANAGEMENT_DASHBOARD_BASE}/federation-details`,
  organizationDetails: `${MANAGEMENT_DASHBOARD_BASE}/organization-details`,
  adserverDetails: `${MANAGEMENT_DASHBOARD_BASE}/adserver-details`,
} as const;

export type ManagementDetailsVariant =
  | "federation"
  | "organization"
  | "adserver";

type SelectOption = { label: string; value: string };

export type DetailsFilterDefinition = {
  id: string;
  selectName: string;
  apiKey: string;
  disabled?: boolean;
  style?: CSSProperties;
  preserveOptionCase?: boolean;
  getOptions: (dashboardData: DashboardData | null) => SelectOption[];
};

export type ManagementDetailsPageConfig = {
  variant: ManagementDetailsVariant;
  path: string;
  pageTitle: string;
  sectionTitle: string;
  tableTitle: string;
  tableColumns: DataTableColumn[];
  filters: DetailsFilterDefinition[];
};

const allEventTypes = (dashboardData: DashboardData | null): SelectOption[] =>
  Array.from(
    new Set((dashboardData?.eventType ?? []).map((item) => item.event_type)),
  ).map((option) => ({ label: option, value: option }));

const federationSelectOptions = (
  dashboardData: DashboardData | null,
): SelectOption[] =>
  Array.from(
    new Map(
      (dashboardData?.federationMonth ?? []).map((item) => [
        item.federation_id,
        {
          value: item.federation_id,
          label: item.federation_name || item.federation_id,
        },
      ]),
    ).values(),
  );

const organizationSelectOptions = (
  dashboardData: DashboardData | null,
): SelectOption[] =>
  Array.from(
    new Map(
      (dashboardData?.organisationMonth ?? []).map((item) => [
        item.organization_id,
        {
          value: item.organization_id,
          label: item.organization_name || item.organization_id,
        },
      ]),
    ).values(),
  );

const adserverTypeOptions = (
  dashboardData: DashboardData | null,
): SelectOption[] =>
  Array.from(
    new Set(
      (dashboardData?.adserverMonth ?? []).map(
        (item) => item.destination_adserver_type,
      ),
    ),
  ).map((option) => ({
    label: formatAdserverType(option),
    value: option,
  }));

export const withAll = (options: SelectOption[]): SelectOption[] => [
  { label: "All", value: "ALL" },
  ...options,
];

const eventTypeFilter = (id = "event_type"): DetailsFilterDefinition => ({
  id,
  selectName: "Event Type",
  apiKey: "event_type",
  getOptions: (dashboardData) => withAll(allEventTypes(dashboardData)),
});

const federationFilter: DetailsFilterDefinition = {
  id: "federation_id",
  selectName: "Federations",
  apiKey: "federation_id",
  getOptions: (dashboardData) =>
    withAll(federationSelectOptions(dashboardData)),
};

const organizationFilter: DetailsFilterDefinition = {
  id: "organization_id",
  selectName: "Organizations",
  apiKey: "organization_id",
  getOptions: (dashboardData) =>
    withAll(organizationSelectOptions(dashboardData)),
};

const adserverTypeFilter = (selectName: string): DetailsFilterDefinition => ({
  id: "destination_adserver_type",
  selectName,
  apiKey: "destination_adserver_type",
  preserveOptionCase: true,
  getOptions: (dashboardData) => withAll(adserverTypeOptions(dashboardData)),
});

/** Month = aggregated rows; day = daily breakdown when a table month pill is selected. */
export type DetailsTableTimeGranularity = "month" | "day";

const formatEventType = (val: string | number) =>
  String(val)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()
  ).replace("Marked Engaged", "Marked/Engaged");

const formatCount = (val: string | number) => {
  return Number(val).toLocaleString("en-US");
};

const getMetricColumns = (
  variant: ManagementDetailsVariant,
): DataTableColumn[] => [
  { label: "Event Type", key: "eventType", formatter: formatEventType },
  { label: "Audience Volume", key: "totalCount", formatter: formatCount },
  {
    label: variant === "adserver" ? "Activation Count" : "Queries Executed",
    key: "queryCount",
  },
];

const DETAILS_ENTITY_COLUMN: Record<ManagementDetailsVariant, DataTableColumn> =
  {
    federation: { label: "Federation Name", key: "entityId" },
    organization: { label: "Organization Name", key: "entityId" },
    adserver: {
      label: "Destination ad server",
      key: "adserverType",
      formatter: (v) => formatAdserverType(v),
    },
  };

const DETAILS_TIME_COLUMN: Record<
  DetailsTableTimeGranularity,
  DataTableColumn
> = {
  month: { label: "Month", key: "timePeriodMonth" },
  day: { label: "Date", key: "date" },
};

export function getDetailsPageTableColumns(
  variant: ManagementDetailsVariant,
  timeGranularity: DetailsTableTimeGranularity,
): DataTableColumn[] {
  return [
    DETAILS_ENTITY_COLUMN[variant],
    DETAILS_TIME_COLUMN[timeGranularity],
    ...getMetricColumns(variant),
  ];
}

export const FEDERATION_DETAILS_CONFIG: ManagementDetailsPageConfig = {
  variant: "federation",
  path: MANAGEMENT_DASHBOARD_PATHS.federationDetails,
  pageTitle: "Management Dashboard - Detailed Page",
  sectionTitle: "Federation Wise Summary",
  tableTitle: "Federation Summary Table",
  tableColumns: getDetailsPageTableColumns("federation", "month"),
  filters: [
    eventTypeFilter(),
    federationFilter,
    adserverTypeFilter("Ad Servers"),
  ],
};

export const ORGANIZATION_DETAILS_CONFIG: ManagementDetailsPageConfig = {
  variant: "organization",
  path: MANAGEMENT_DASHBOARD_PATHS.organizationDetails,
  pageTitle: "Management Dashboard - Detailed Page",
  sectionTitle: "Organization Summary",
  tableTitle: "Organization Summary",
  tableColumns: getDetailsPageTableColumns("organization", "month"),
  filters: [eventTypeFilter(), organizationFilter],
};

export const ADSERVER_DETAILS_CONFIG: ManagementDetailsPageConfig = {
  variant: "adserver",
  path: MANAGEMENT_DASHBOARD_PATHS.adserverDetails,
  pageTitle: "Management Dashboard - Detailed Page",
  sectionTitle: "Ad Server Summary",
  tableTitle: "Ad Server Summary",
  tableColumns: getDetailsPageTableColumns("adserver", "month"),
  filters: [adserverTypeFilter("Ad Servers")],
};

const CONFIGS: ManagementDetailsPageConfig[] = [
  FEDERATION_DETAILS_CONFIG,
  ORGANIZATION_DETAILS_CONFIG,
  ADSERVER_DETAILS_CONFIG,
];

export function getManagementDetailsConfig(
  pathname: string,
): ManagementDetailsPageConfig {
  return CONFIGS.find((c) => c.path === pathname) ?? FEDERATION_DETAILS_CONFIG;
}

export function initialFilterValuesForConfig(
  pageConfig: ManagementDetailsPageConfig,
): Record<string, string> {
  const filterRecord: Record<string, string> = {};
  pageConfig.filters.forEach((currentFilter) => {
    filterRecord[currentFilter.id] = currentFilter.disabled ? "na" : "";
  });
  return filterRecord;
}

export function buildDetailsApiQueryParams(
  filters: DetailsFilterDefinition[],
  filterValues: Record<string, string>,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const currentFilter of filters) {
    if (currentFilter.disabled) continue;
    const value = filterValues[currentFilter.id];
    if (!value || value === "ALL") continue;
    params[currentFilter.apiKey] = value;
  }
  return params;
}
